// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Form, Icon, Space, Styled} from '@supermove/components';
import {getQueryName, gql} from '@supermove/graphql';
import {ResponsiveType, useMemo, useMountEffect, useResponsive} from '@supermove/hooks';
import {UserModel, TextMessageTemplateModel, ProjectModel} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';

// App
import DropdownButton from '@shared/design/components/Button/DropdownButton';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import FieldInput from '@shared/design/components/Field/FieldInput';
import TextTooltip from '@shared/design/components/TextTooltip';
import TextMessageSystemKind from '@shared/modules/Sms/enums/TextMessageSystemKind';
import SendTextMessageToUserForm from '@shared/modules/Sms/forms/SendTextMessageToUserForm';
import useSendTextMessageToUserMutation from '@shared/modules/Sms/hooks/useSendTextMessageToUserMutation';
import EvaluateAndReplaceVariablesForm from '@shared/modules/Variable/forms/EvaluateAndReplaceVariablesForm';
import useEvaluateAndReplaceVariablesMutation from '@shared/modules/Variable/hooks/useEvaluateAndReplaceVariablesMutation';
import CommunicationInboxProjects from 'modules/Communication/CommunicationInbox/ConversationList/CommunicationInboxProjects';
import MissingCommunicationInfo from 'modules/Communication/CommunicationInbox/ProjectConversation/components/MissingCommunicationInfo';

const Container = Styled.View`
  height: 156px;
`;

const Loading = Styled.Loading`
`;

const SelectTemplateContainer = Styled.View`
`;

const IconButtonV2 = Styled.ButtonV2<{backgroundColor: string}>`
  height: 24px;
  width: 24px;
  border-radius: 14px;
  background-color: ${({backgroundColor}) => backgroundColor || colors.blue.interactive};
  align-items: center;
  justify-content: center;
`;

const TextTemplateContainer = Styled.View`
  flex-direction: row;
  padding-horizontal: 16px;
  padding-bottom: 12px;
  align-items: center;
  justify-content: space-between;
`;

const PaddingContainer = Styled.View`
  padding-horizontal: 16px;
`;

const CharacterCountText = Styled.Text<{color: string}>`
  ${Typography.Responsive.Body}
  color: ${({color}) => color || colors.gray.secondary};
`;

const CommunicationChatDrawerTextTemplateDropdown = ({
  form,
  textMessageTemplates,
  handlePreviewSubmit,
  previewTextTemplateForm,
  responsive,
}: {
  form: Form<{
    sendTextMessageToUserForm: {
      organizationId: string;
      recipientId: string;
      senderId: string;
      body: string;
      textMessageTemplateId: string;
      bodyHeader: string;
    };
  }>;
  textMessageTemplates: TextMessageTemplateModel[];
  handlePreviewSubmit: () => void;
  previewTextTemplateForm: Form<{
    evaluateAndReplaceVariablesForm: {
      text: string;
    };
  }>;
  responsive: ResponsiveType;
}) => {
  const textMessageTemplateMap = useMemo(
    () =>
      Object.fromEntries(
        textMessageTemplates.map((textMessageTemplate) => [
          textMessageTemplate.id,
          textMessageTemplate.body,
        ]),
      ),
    [textMessageTemplates],
  );

  const sortedCustomTemplates = useMemo(() => {
    return textMessageTemplates
      .filter((textMessageTemplate) => {
        return textMessageTemplate.systemTemplateKind === TextMessageSystemKind.CUSTOM;
      })
      .map((textMessageTemplate) => {
        return {
          text: textMessageTemplate.name,
          value: _.toNumber(textMessageTemplate.id),
          onPress: () => {
            previewTextTemplateForm.setFieldValue(
              'evaluateAndReplaceVariablesForm.text',
              textMessageTemplateMap[textMessageTemplate.id],
            );

            // Resets the text template id if the user wipes out the body
            if (_.isEmpty(textMessageTemplate.id)) {
              form.setFieldValue('sendTextMessageToUserForm.textMessageTemplateId', null);
            }

            setTimeout(handlePreviewSubmit, 0);
          },
        };
      })
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [textMessageTemplates]);

  return (
    <TextTooltip text={responsive.desktop ? 'Select template' : undefined} placement={'top'}>
      <SelectTemplateContainer>
        <DropdownButton
          ButtonComponent={TertiaryButton}
          actions={sortedCustomTemplates}
          isVisibleArrow={false}
          isPortaled
          iconLeft={Icon.FilePen}
          buttonComponentProps={{textColor: colors.gray.secondary}}
          menuWidth={200}
          menuPosition={'TOP'}
        />
      </SelectTemplateContainer>
    </TextTooltip>
  );
};

const CommunicationInboxFooterSendText = ({
  customer,
  viewer,
  refetch,
  project,
  textMessageTemplates,
  selectedTextMessageTemplate,
}: {
  customer: UserModel;
  viewer: UserModel;
  refetch: () => void;
  project: ProjectModel;
  textMessageTemplates: any[];
  selectedTextMessageTemplate: any | null;
}) => {
  const responsive = useResponsive();
  const sendTextMessageToUserForm = SendTextMessageToUserForm.newForCommunicationChatButton({
    organizationId: customer.organizationId,
    projectId: project.id,
    recipient: customer,
    sender: viewer,
  });
  const resetForm = () => form.setFieldValue('sendTextMessageToUserForm.body', '');
  const {form, handleSubmit, submitting} = useSendTextMessageToUserMutation({
    sendTextMessageToUserForm,
    onSuccess: () => {
      refetch();
      resetForm();
    },
    onError: (errors) => console.log({errors}),
    refetchQueries: [getQueryName(CommunicationInboxProjects.query)],
  });

  const evaluateAndReplaceVariablesForm = EvaluateAndReplaceVariablesForm.new({
    projectId: project.id,
    organizationId: customer.organizationId,
  });

  const {
    form: previewTextTemplateForm,
    handleSubmit: handlePreviewSubmit,
    submitting: previewSubmitting,
  } = useEvaluateAndReplaceVariablesMutation({
    evaluateAndReplaceVariablesForm,
    onSuccess: ({text}: {text: string}) => {
      const bodyHeader = _.get(form.values, 'sendTextMessageToUserForm.bodyHeader');
      form.setFieldValue('sendTextMessageToUserForm.body', bodyHeader + text);
    },
    onError: (errors) => console.log({errors}),
  });

  useMountEffect(() => {
    if (selectedTextMessageTemplate && selectedTextMessageTemplate.body !== '') {
      previewTextTemplateForm.setFieldValue(
        'evaluateAndReplaceVariablesForm.text',
        selectedTextMessageTemplate.body,
      );

      setTimeout(() => handlePreviewSubmit(), 0);
    }
  });

  const field = 'sendTextMessageToUserForm.body';

  const body = _.get(form.values, field);
  const noPhoneNumber = !customer.phoneNumber;
  const isDisabled = !body || submitting;

  if (noPhoneNumber) {
    return (
      <MissingCommunicationInfo
        linkText={'Add a phone number'}
        text={`to send text messages ${customer.fullName}`}
        focusField='phoneNumber'
        onSuccess={refetch}
        project={project}
        user={customer}
      />
    );
  }

  return (
    <Container>
      <PaddingContainer>
        <FieldInput.Memoized
          {...form}
          index={1}
          name={field}
          isResponsive
          handleCommandEnter={handleSubmit}
          input={{
            placeholder: 'Reply with SMS',
            multiline: true,
            style: {
              fontWeight: '500',
              fontSize: responsive.desktop ? '14px' : '16px',
              color: colors.gray.primary,
              backgroundColor: colors.white,
              paddingLeft: 0,
              paddingRight: 0,
              borderWidth: 0,
              borderRadius: 0,
              paddingVertical: 18,
              minHeight: 96,
            },
          }}
        />
      </PaddingContainer>
      <Space height={4} />
      <Space flex={1} />
      <TextTemplateContainer>
        <CommunicationChatDrawerTextTemplateDropdown
          form={form}
          textMessageTemplates={textMessageTemplates}
          handlePreviewSubmit={handlePreviewSubmit}
          previewTextTemplateForm={previewTextTemplateForm}
          responsive={responsive}
        />
        <Space flex={1} />
        <CharacterCountText
          responsive={responsive}
          color={body.length > 1500 ? colors.red.warning : colors.gray.secondary}
        >
          {`${body.length}/1500`}
        </CharacterCountText>
        <Space width={24} />
        {submitting || previewSubmitting ? (
          <Loading />
        ) : (
          <TextTooltip
            text={isDisabled ? 'Please enter a message' : responsive.desktop ? 'Send' : undefined}
            placement={'topRight'}
          >
            <IconButtonV2
              onPress={handleSubmit}
              disabled={isDisabled}
              backgroundColor={colors.blue.interactive}
            >
              <Icon source={Icon.ArrowUp} size={14} color={colors.white} />
            </IconButtonV2>
          </TextTooltip>
        )}
      </TextTemplateContainer>
    </Container>
  );
};

CommunicationInboxFooterSendText.fragment = gql`
  ${SendTextMessageToUserForm.newForCommunicationChatButton.fragment}
  ${MissingCommunicationInfo.fragment}

  fragment CommunicationInboxFooterSendText_User on User {
    id
    phoneNumber
    organizationId
    ...SendTextMessageToUserForm_newForCommunicationChatButton_recipient
    ...MissingCommunicationInfo_User
  }

  fragment CommunicationInboxFooterSendText_TextMessageTemplate on TextMessageTemplate {
    id
    name
    body
    systemTemplateKind
  }

  fragment CommunicationInboxFooterSendText_Viewer on User {
    id
    fullName
    ...SendTextMessageToUserForm_newForCommunicationChatButton_sender
  }

  fragment CommunicationInboxFooterSendText_Project on Project {
    id
    ...MissingCommunicationInfo
  }
`;

export default CommunicationInboxFooterSendText;
